/**
 * `spookyHash.ts` — SpookyHash V2 (Bob Jenkins, 2012), 128-bit variant.
 *
 * Unity's `Hash128` is SpookyHash V2 under the hood, and Unity's
 * `SharedLinkData.GenerateID` builds every shared-link row id from it — so a
 * web-created link row is only findable by the desktop client if this file
 * reproduces that hash bit for bit (see `models/data/sharedLinkId.ts`).
 *
 * This is a transliteration of `SpookyV2.cpp`'s `Hash128` / `Short` /
 * `Mix` / `End` / `ShortMix` / `ShortEnd`, with two deliberate web choices:
 *
 *  - **`BigInt` for all 64-bit arithmetic.** The inputs here are a GUID and an
 *    email — well under 200 bytes — so the cost of `BigInt` is irrelevant and
 *    the correctness of 64-bit rotates and wrapping adds is not. A 32-bit
 *    pair implementation would be faster and is the usual JS trap: one missed
 *    carry reproduces 99% of test vectors and none of the ids that matter.
 *  - **Little-endian reads**, matching the reference's `union { uint64 *p64;
 *    … }` aliasing on every platform Unity ships to.
 *
 * The seed is IN/OUT exactly as in the reference (`*hash1` / `*hash2`), which
 * is what lets `sharedLinkId.ts` express Unity's chained `Hash128.Append`.
 */

const MASK64 = (1n << 64n) - 1n;

/** `sc_const` — a constant "which is not zero and is odd, and not very regular". */
export const SPOOKY_CONST = 0xdeadbeefdeadbeefn;

/** `sc_numVars` — number of u64 state variables in the long-message mix. */
const K_NUM_VARS = 12;
/** `sc_blockSize` — bytes consumed per `Mix` (12 × 8). */
const K_BLOCK_SIZE = K_NUM_VARS * 8;
/** `sc_bufSize` — messages shorter than this take the `Short` path. */
const K_BUF_SIZE = 2 * K_BLOCK_SIZE;

function rot64(x: bigint, k: number): bigint {
  return ((x << BigInt(k)) | (x >> BigInt(64 - k))) & MASK64;
}

function add(a: bigint, b: bigint): bigint {
  return (a + b) & MASK64;
}

/**
 * SpookyHash V2 `Hash128(message, length, &hash1, &hash2)`.
 *
 * @param message Bytes to hash.
 * @param seed1 IN: seed 1. The reference reads and then overwrites `*hash1`;
 *   the returned tuple's first element is the overwritten value.
 * @param seed2 IN: seed 2, likewise for `*hash2`.
 * @returns `[hash1, hash2]` as unsigned 64-bit `BigInt`s.
 */
export function spookyHash128(
  message: Uint8Array,
  seed1: bigint = 0n,
  seed2: bigint = 0n,
): [bigint, bigint] {
  const length = message.length;
  if (length < K_BUF_SIZE) return short(message, seed1 & MASK64, seed2 & MASK64);

  const view = new DataView(message.buffer, message.byteOffset, message.byteLength);
  const u64 = (offset: number): bigint => view.getBigUint64(offset, true);

  const h0 = seed1 & MASK64;
  const h1 = seed2 & MASK64;
  let s: bigint[] = [
    h0, h1, SPOOKY_CONST,
    h0, h1, SPOOKY_CONST,
    h0, h1, SPOOKY_CONST,
    h0, h1, SPOOKY_CONST,
  ];

  const wholeBlocks = Math.floor(length / K_BLOCK_SIZE);
  const end = wholeBlocks * K_BLOCK_SIZE;
  const data = new Array<bigint>(K_NUM_VARS);
  for (let offset = 0; offset < end; offset += K_BLOCK_SIZE) {
    for (let i = 0; i < K_NUM_VARS; i++) data[i] = u64(offset + i * 8);
    s = mix(data, s);
  }

  // Last partial block: copy into a zeroed block, stamp the remainder length
  // into the final byte, and run `End`.
  const remainder = length - end;
  const buf = new Uint8Array(K_BLOCK_SIZE);
  buf.set(message.subarray(end, end + remainder), 0);
  buf[K_BLOCK_SIZE - 1] = remainder;
  const bufView = new DataView(buf.buffer);
  for (let i = 0; i < K_NUM_VARS; i++) data[i] = bufView.getBigUint64(i * 8, true);
  s = endBlock(data, s);
  return [s[0], s[1]];
}

/** `Short()` — messages under `sc_bufSize` (192) bytes. */
function short(message: Uint8Array, seed1: bigint, seed2: bigint): [bigint, bigint] {
  const length = message.length;
  const view = new DataView(message.buffer, message.byteOffset, message.byteLength);
  const u64 = (offset: number): bigint => view.getBigUint64(offset, true);
  const u32 = (offset: number): bigint => BigInt(view.getUint32(offset, true));
  const u8 = (offset: number): bigint => BigInt(message[offset]);

  let remainder = length % 32;
  let a = seed1;
  let b = seed2;
  let c = SPOOKY_CONST;
  let d = SPOOKY_CONST;
  let offset = 0;

  if (length > 15) {
    const end = Math.floor(length / 32) * 32;
    // Handle all complete sets of 32 bytes.
    for (; offset < end; offset += 32) {
      c = add(c, u64(offset));
      d = add(d, u64(offset + 8));
      [a, b, c, d] = shortMix(a, b, c, d);
      a = add(a, u64(offset + 16));
      b = add(b, u64(offset + 24));
    }
    // Handle the case of 16+ remaining bytes.
    if (remainder >= 16) {
      c = add(c, u64(offset));
      d = add(d, u64(offset + 8));
      [a, b, c, d] = shortMix(a, b, c, d);
      offset += 16;
      remainder -= 16;
    }
  }

  // Handle the last 0..15 bytes, and its length. The reference is a
  // fall-through `switch` in four groups (15..12, 11..8, 7..4, 3..1, then 0);
  // each group folds its loose bytes in byte-position order and then its
  // whole word. Written as ranges here because the project forbids case
  // fall-through, but byte-for-byte the same additions.
  d = add(d, (BigInt(length) << 56n) & MASK64);
  if (remainder >= 12) {
    // cases 15, 14, 13 → d += byte[i] << (8 * (i - 8)); then case 12.
    for (let i = 12; i < remainder; i++) d = add(d, u8(offset + i) << BigInt(8 * (i - 8)));
    d = add(d, u32(offset + 8));
    c = add(c, u64(offset));
  } else if (remainder >= 8) {
    // cases 11, 10, 9 → d += byte[i] << (8 * (i - 8)); then case 8.
    for (let i = 8; i < remainder; i++) d = add(d, u8(offset + i) << BigInt(8 * (i - 8)));
    c = add(c, u64(offset));
  } else if (remainder >= 4) {
    // cases 7, 6, 5 → c += byte[i] << (8 * i); then case 4.
    for (let i = 4; i < remainder; i++) c = add(c, u8(offset + i) << BigInt(8 * i));
    c = add(c, u32(offset));
  } else if (remainder >= 1) {
    // cases 3, 2, 1 → c += byte[i] << (8 * i).
    for (let i = 0; i < remainder; i++) c = add(c, u8(offset + i) << BigInt(8 * i));
  } else {
    // case 0.
    c = add(c, SPOOKY_CONST);
    d = add(d, SPOOKY_CONST);
  }
  [a, b, c, d] = shortEnd(a, b, c, d);
  return [a, b];
}

/** `ShortMix(h0,h1,h2,h3)`. */
function shortMix(h0: bigint, h1: bigint, h2: bigint, h3: bigint): [bigint, bigint, bigint, bigint] {
  h2 = rot64(h2, 50); h2 = add(h2, h3); h0 ^= h2;
  h3 = rot64(h3, 52); h3 = add(h3, h0); h1 ^= h3;
  h0 = rot64(h0, 30); h0 = add(h0, h1); h2 ^= h0;
  h1 = rot64(h1, 41); h1 = add(h1, h2); h3 ^= h1;
  h2 = rot64(h2, 54); h2 = add(h2, h3); h0 ^= h2;
  h3 = rot64(h3, 48); h3 = add(h3, h0); h1 ^= h3;
  h0 = rot64(h0, 38); h0 = add(h0, h1); h2 ^= h0;
  h1 = rot64(h1, 37); h1 = add(h1, h2); h3 ^= h1;
  h2 = rot64(h2, 62); h2 = add(h2, h3); h0 ^= h2;
  h3 = rot64(h3, 34); h3 = add(h3, h0); h1 ^= h3;
  h0 = rot64(h0, 5);  h0 = add(h0, h1); h2 ^= h0;
  h1 = rot64(h1, 36); h1 = add(h1, h2); h3 ^= h1;
  return [h0, h1, h2, h3];
}

/** `ShortEnd(h0,h1,h2,h3)`. */
function shortEnd(h0: bigint, h1: bigint, h2: bigint, h3: bigint): [bigint, bigint, bigint, bigint] {
  h3 ^= h2; h2 = rot64(h2, 15); h3 = add(h3, h2);
  h0 ^= h3; h3 = rot64(h3, 52); h0 = add(h0, h3);
  h1 ^= h0; h0 = rot64(h0, 26); h1 = add(h1, h0);
  h2 ^= h1; h1 = rot64(h1, 51); h2 = add(h2, h1);
  h3 ^= h2; h2 = rot64(h2, 28); h3 = add(h3, h2);
  h0 ^= h3; h3 = rot64(h3, 9);  h0 = add(h0, h3);
  h1 ^= h0; h0 = rot64(h0, 47); h1 = add(h1, h0);
  h2 ^= h1; h1 = rot64(h1, 54); h2 = add(h2, h1);
  h3 ^= h2; h2 = rot64(h2, 32); h3 = add(h3, h2);
  h0 ^= h3; h3 = rot64(h3, 25); h0 = add(h0, h3);
  h1 ^= h0; h0 = rot64(h0, 63); h1 = add(h1, h0);
  return [h0, h1, h2, h3];
}

/** `Mix(data, s0..s11)` — one 96-byte block of the long-message path. */
function mix(data: ReadonlyArray<bigint>, s: ReadonlyArray<bigint>): bigint[] {
  let [s0, s1, s2, s3, s4, s5, s6, s7, s8, s9, s10, s11] = s;
  s0 = add(s0, data[0]);    s2 ^= s10; s11 ^= s0;  s0 = rot64(s0, 11);   s11 = add(s11, s1);
  s1 = add(s1, data[1]);    s3 ^= s11; s0 ^= s1;   s1 = rot64(s1, 32);   s0 = add(s0, s2);
  s2 = add(s2, data[2]);    s4 ^= s0;  s1 ^= s2;   s2 = rot64(s2, 43);   s1 = add(s1, s3);
  s3 = add(s3, data[3]);    s5 ^= s1;  s2 ^= s3;   s3 = rot64(s3, 31);   s2 = add(s2, s4);
  s4 = add(s4, data[4]);    s6 ^= s2;  s3 ^= s4;   s4 = rot64(s4, 17);   s3 = add(s3, s5);
  s5 = add(s5, data[5]);    s7 ^= s3;  s4 ^= s5;   s5 = rot64(s5, 28);   s4 = add(s4, s6);
  s6 = add(s6, data[6]);    s8 ^= s4;  s5 ^= s6;   s6 = rot64(s6, 39);   s5 = add(s5, s7);
  s7 = add(s7, data[7]);    s9 ^= s5;  s6 ^= s7;   s7 = rot64(s7, 57);   s6 = add(s6, s8);
  s8 = add(s8, data[8]);    s10 ^= s6; s7 ^= s8;   s8 = rot64(s8, 55);   s7 = add(s7, s9);
  s9 = add(s9, data[9]);    s11 ^= s7; s8 ^= s9;   s9 = rot64(s9, 54);   s8 = add(s8, s10);
  s10 = add(s10, data[10]); s0 ^= s8;  s9 ^= s10;  s10 = rot64(s10, 22); s9 = add(s9, s11);
  s11 = add(s11, data[11]); s1 ^= s9;  s10 ^= s11; s11 = rot64(s11, 46); s10 = add(s10, s0);
  return [s0, s1, s2, s3, s4, s5, s6, s7, s8, s9, s10, s11];
}

/** `EndPartial(h0..h11)`. */
function endPartial(h: ReadonlyArray<bigint>): bigint[] {
  let [h0, h1, h2, h3, h4, h5, h6, h7, h8, h9, h10, h11] = h;
  h11 = add(h11, h1); h2 ^= h11; h1 = rot64(h1, 44);
  h0 = add(h0, h2);   h3 ^= h0;  h2 = rot64(h2, 15);
  h1 = add(h1, h3);   h4 ^= h1;  h3 = rot64(h3, 34);
  h2 = add(h2, h4);   h5 ^= h2;  h4 = rot64(h4, 21);
  h3 = add(h3, h5);   h6 ^= h3;  h5 = rot64(h5, 38);
  h4 = add(h4, h6);   h7 ^= h4;  h6 = rot64(h6, 33);
  h5 = add(h5, h7);   h8 ^= h5;  h7 = rot64(h7, 10);
  h6 = add(h6, h8);   h9 ^= h6;  h8 = rot64(h8, 13);
  h7 = add(h7, h9);   h10 ^= h7; h9 = rot64(h9, 38);
  h8 = add(h8, h10);  h11 ^= h8; h10 = rot64(h10, 53);
  h9 = add(h9, h11);  h0 ^= h9;  h11 = rot64(h11, 42);
  h10 = add(h10, h0); h1 ^= h10; h0 = rot64(h0, 54);
  return [h0, h1, h2, h3, h4, h5, h6, h7, h8, h9, h10, h11];
}

/** `End(data, h0..h11)` — V2 folds the final block in, then mixes three times. */
function endBlock(data: ReadonlyArray<bigint>, h: ReadonlyArray<bigint>): bigint[] {
  let s = h.map((value, i) => add(value, data[i]));
  s = endPartial(s);
  s = endPartial(s);
  s = endPartial(s);
  return s;
}

/**
 * Render a 128-bit hash the way Unity's `Hash128.ToString()` does: the 16
 * bytes of the `{u64 h1, u64 h2}` union in MEMORY order on a little-endian
 * machine — i.e. `h1`'s bytes least-significant first, then `h2`'s — as 32
 * lowercase hex characters. Unity's implementation is `BytesToHexString` over
 * the union, not a numeric `%016x` print, which is why the nibble order looks
 * reversed next to the `BigInt` value.
 */
export function hash128ToUnityHex(h1: bigint, h2: bigint): string {
  const bytes = new Uint8Array(16);
  const view = new DataView(bytes.buffer);
  view.setBigUint64(0, h1 & MASK64, true);
  view.setBigUint64(8, h2 & MASK64, true);
  let out = "";
  for (const byte of bytes) out += byte.toString(16).padStart(2, "0");
  return out;
}
